import type { AppError, AsyncResult, Result } from '@shared/utils';
import { err, ok } from '@shared/utils';
import { RuntimeBoundFacade, BaseRuntimeManager } from '@shared/core';
import type { PackageResourceConfig } from '@shared/core';
import type { TelehealthEnvironmentConfig } from '../config/interfaces/TelehealthEnvironmentConfig';
import type { TelehealthMeetingComponentProps } from '../interfaces/TelehealthMeetingComponentProps';
import type { TelehealthReservationInput } from '../interfaces/TelehealthReservationInput';
import type { TelehealthReservationResult } from '../interfaces/TelehealthReservationResult';
import type { TelehealthSessionCredentials } from '../interfaces/TelehealthSessionCredentials';
import type { TelehealthSessionInput } from '../interfaces/TelehealthSessionInput';
import type { TelehealthAdapter } from '../interfaces/TelehealthAdapter';
import type { TelehealthProviderName } from '../types/TelehealthProviderName';
import type { ComponentType } from 'react';

const TELEHEALTH_ERROR_CODES = {
  NO_ADAPTER: 'TELEHEALTH_NO_ADAPTER',
} as const;

const TELEHEALTH_MESSAGES = {
  NO_ADAPTER: 'No telehealth adapter configured',
} as const;

class TelehealthInstance {
  private readonly runtimeManager = new BaseRuntimeManager<TelehealthEnvironmentConfig>();

  setConfig(config: PackageResourceConfig<TelehealthEnvironmentConfig>): void {
    this.runtimeManager.setConfig(config);
  }

  private getAdapter(): Result<TelehealthAdapter, AppError> {
    const planResult = this.runtimeManager.resolveExecutionPlan();
    if (planResult.isErr) {
      return err(planResult.error);
    }
    const { envConfig } = planResult.value;
    const adapter = envConfig.adapters[0];
    if (!adapter) {
      return err({
        errorCode: TELEHEALTH_ERROR_CODES.NO_ADAPTER,
        errorMessage: TELEHEALTH_MESSAGES.NO_ADAPTER,
      });
    }
    return ok(adapter);
  }

  async mintSessionCredentials(
    input: TelehealthSessionInput
  ): AsyncResult<TelehealthSessionCredentials, AppError> {
    const adapterResult = this.getAdapter();
    if (adapterResult.isErr) {
      return err(adapterResult.error);
    }
    return adapterResult.value.mintSessionCredentials(input);
  }

  async validateRoom(
    input: TelehealthReservationInput
  ): AsyncResult<TelehealthReservationResult, AppError> {
    const adapterResult = this.getAdapter();
    if (adapterResult.isErr) {
      return err(adapterResult.error);
    }
    return adapterResult.value.validateRoom(input);
  }

  async getMeetingComponent(
    props: TelehealthMeetingComponentProps
  ): AsyncResult<ComponentType<TelehealthMeetingComponentProps>, AppError> {
    const adapterResult = this.getAdapter();
    if (adapterResult.isErr) {
      return err(adapterResult.error);
    }
    return adapterResult.value.getMeetingComponent(props);
  }

  getProviderName(): AsyncResult<TelehealthProviderName, AppError> {
    const adapterResult = this.getAdapter();
    if (adapterResult.isErr) {
      return Promise.resolve(err(adapterResult.error));
    }
    return Promise.resolve(ok(adapterResult.value.providerName));
  }
}

/**
 * @group Classes
 *
 * @summary
 * <blockquote>DI facade for telehealth operations. Queues calls before bootstrap and
 * delegates to the registered adapter after bootstrap.</blockquote>
 *
 * @example
 * ```typescript
 * import { Telehealth } from '@shared/telehealth';
 *
 * const creds = await Telehealth.mintSessionCredentials({ ... });
 * const component = await Telehealth.getMeetingComponent({ ... });
 * ```
 */
export class Telehealth extends RuntimeBoundFacade<TelehealthInstance> {
  protected readonly facadeKey = 'telehealth';

  private static facade = new Telehealth();

  private constructor() {
    super();
  }

  static configureRuntime(config: PackageResourceConfig<TelehealthEnvironmentConfig>): void {
    const instance = new TelehealthInstance();
    instance.setConfig(config);
    Telehealth.facade.setInstance(instance);
  }

  static async mintSessionCredentials(
    input: TelehealthSessionInput
  ): AsyncResult<TelehealthSessionCredentials, AppError> {
    return Telehealth.facade.callWithInstanceResult((instance) =>
      instance.mintSessionCredentials(input)
    );
  }

  static async validateRoom(
    input: TelehealthReservationInput
  ): AsyncResult<TelehealthReservationResult, AppError> {
    return Telehealth.facade.callWithInstanceResult((instance) =>
      instance.validateRoom(input)
    );
  }

  static async getMeetingComponent(
    props: TelehealthMeetingComponentProps
  ): AsyncResult<ComponentType<TelehealthMeetingComponentProps>, AppError> {
    return Telehealth.facade.callWithInstanceResult((instance) =>
      instance.getMeetingComponent(props)
    );
  }

  static async getProviderName(): AsyncResult<TelehealthProviderName, AppError> {
    return Telehealth.facade.callWithInstanceResult((instance) =>
      instance.getProviderName()
    );
  }

  static resetInstance(): void {
    Telehealth.facade.resetInstance();
    Telehealth.facade = new Telehealth();
  }

  static clearRuntime(): void {
    Telehealth.resetInstance();
  }
}
